import Vue from 'vue';
import router from '@/router';
import { $deleteFromArray, $handleError, $updateOrAddSilent, $updateManually, $updateOrAdd } from '@/utils';
import taskService from '@Task/service/gantt.service';
import { moveRefusalMessage } from '@Task/utils/kanbanMove';
import { planTaskPatch, hasActiveFilters } from '@Task/utils/taskPatch';

/** How long a refused move's message stays, as $handleError's for a refused request. */
const MOVE_REFUSED_NOTIFY = Object.freeze({ position: 'top-right', timeout: 6 * 1000 });


export const TASK = {
  namespaced: true,
  state: {
    entries: [],
    tasks:[],
    total: 0,
    loading: false,
    updateLoading: false,
    options: {},
    currentEvent: null,
    isolatedTasks: [],
    entriesCollection: [],
    routeToTaskFired: false,
    lastIsolatedStatusId: null,
    // Tracks which taskTypeId's data is currently in `entries`. Used by
    // Task/views/index.vue's $route watcher to skip the heavy refetch when
    // navigating back to TaskBoard from a deep-link round-trip (the data is
    // already loaded). Cleared by TASK/CLEAR.
    lastLoadedTaskTypeId: null,
  },
  getters: {
    GET_ENTRIES(state) {
      return state.entries;
    },
    GET_TASKS(state) {
      return state.entries?.tasks?.items ?? [];
    },
    GET_LINKS(state) {
      return state.entries?.links;
    },
    
    GET_TOTAL(state) {
      return state.total;
    },
    GET_LOADING(state) {
      return state.loading;
    },
    GET_UPDATE_LOADING(state) {
      return state.updateLoading;
    },

    GET_OPTIONS(state) {
      return state.options;
    },
    CURRENT_EVENT(state) {
      return state.currentEvent;
    },

    GET_ISOLATED_TASKS(state) {
      return state.isolatedTasks;
    },
    GET_ENTRIES_COLLECTION(state) {
      return state.entriesCollection;
    },
    GET_ROUTE_TO_PATH_FIRED(state) {
      return state.routeToTaskFired;
    },
    GET_LAST_ISOLATED_STATUS_ID(state) {
      return state.lastIsolatedStatusId;
    },
  },
  mutations: {
    SET_ENTRIES(state, value) {
      state.entries = value;
    },
    SET_CURRENT_EVENT(state, value) {
      state.currentEvent = value;
    },
    SET_TOTAL(state, value) {
      state.total = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },

    SET_UPDATE_LOADING(state, value) {
      state.updateLoading = value;
    },

    SET_OPTIONS(state, value) {
      state.options = value;
    },

    SET_ISOLATED_TASKS(state, value) {
      state.isolatedTasks = value;
    },
    SET_ENTRIES_COLLECTION(state, value) {
      state.entriesCollection = value;
    },
    SET_ROUTE_TO_PATH_FIRED(state, value) {
      state.routeToTaskFired = value
    },
    SET_LAST_ISOLATED_STATUS_ID(state, value) {
      state.lastIsolatedStatusId = value
    },
    SET_LAST_LOADED_TASK_TYPE_ID(state, value) {
      state.lastLoadedTaskTypeId = value;
    },
    // Add or replace a single task in entries.tasks.items. Used by share-intent
    // deep links that fetch one task via TASK/GET_BY_ID and need it findable
    // by ViewerTaskColoring (which filters this.tasks by isolatedTasks) — without
    // dispatching the heavy TASK/GET_DATA which would fetch the whole board.
    // Initializes the entries shape if absent. mapType ensures the task gets
    // the same date/customField normalization as entries from GET_DATA.
    UPSERT_TASK(state, task) {
      if (!state.entries?.tasks?.items) {
        state.entries = { ...(state.entries || {}), tasks: { items: [] } };
      }
      $updateOrAddSilent(state.entries.tasks.items, mapType(task));
    },
  },
  actions: {
    CLEAR({ commit }) {
      commit('SET_ENTRIES', []);
      commit('SET_TOTAL', 0);
      commit('SET_LAST_LOADED_TASK_TYPE_ID', null);
      // Reset per-board filters + search so a newly-opened board starts clean.
      // `options` is a single global object (not keyed by board id), so without
      // this the previous board's filters leak into the next board's data
      // request (GET_DATA spreads state.options last → it overrides the params).
      commit('SET_OPTIONS', {});
      // Reset the isolation SELECTION too. isolatedTasks is board/facility-
      // relative; without this it survives a board (or in-app facility) switch
      // and Kanban's entries() watcher (Kanban.vue:1167) auto-re-isolates the
      // prior board's task on the new one — silently opening the wrong viewer
      // (e.g. 3D auto-opens on a cross-facility task). Safe for the legit
      // back-to-Kanban restore: that round-trip hits the $route cache-skip
      // (index.vue:1302) and never dispatches CLEAR, so pendingRestore still
      // re-isolates as before.
      commit('SET_ISOLATED_TASKS', []);
    },
    GET({ commit, state }, options) {
      commit('SET_LOADING', true);
      return taskService
        .get(Object.assign({}, options || {}, state.options))
        .then((res) => {
          commit('SET_ENTRIES', res.data.items.map(e => mapType(e)));
          commit('SET_TOTAL', res.data.total);
        })
        .catch((err) => {
          $handleError(err);
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    },
    GET_DATA(context, options) {
      return loadBoard(context, options);
    },
    /** GET_DATA without the loading line: the quiet fallback when AVA changed tasks (PATCH_CHANGED). */
    GET_DATA_QUIETLY(context, options) {
      return loadBoard(context, options, { quiet: true });
    },
    /**
     * AVA changed these tasks (AVA-755): read each one and patch it into the board's tasks in place, as a move
     * does (patchSavedTask), so the open board shows it without a reload. A new task joins the list (the Kanban
     * adds its card). Nothing is patched when a patch could be wrong (a filter is on, a task left the board, a
     * read failed): `refetch` then asks the caller to read the list again quietly.
     *
     * @param {{ boardId: string, ids: string[] }} payload the board on screen and the changed tasks to read
     * @returns {Promise<{ ids: string[], refetch: boolean }>} the board's changed tasks
     */
    async PATCH_CHANGED({ state, commit }, { boardId, ids }) {
      const reads = await Promise.allSettled(ids.map((id) => taskService.getById(id)));
      const fetched = reads.filter((read) => read.status === 'fulfilled' && read.value?.data).map((read) => mapType(read.value.data));
      const plan = planTaskPatch({
        boardId,
        current: state.entries?.tasks?.items ?? [],
        fetched,
        filtered: hasActiveFilters(state.options),
      });
      if (plan.refetch || fetched.length < reads.length) return { ids: plan.ids, refetch: true };
      [...plan.update.map((change) => change.task), ...plan.add].forEach((task) => patchSavedTask(commit, task));
      return { ids: plan.ids, refetch: false };
    },
    GET_BY_ID(context, id) {
      return taskService.getById(id);
    },
    /**
     * Batch tag-isolation prefetch for the Task Board.
     * Returns the raw response from POST /api/tasks/getIsolationByIds — a flat
     * list of { taskId, combinedAssets, otherAssets, hasCurrentFacility }.
     * The orchestration layer (taskIsolation.js) is responsible for indexing
     * the result and consuming it from views; we keep this action thin.
     */
    GET_TASKS_ISOLATION_BY_IDS(context, { taskIds, assetId }) {
      return taskService.getIsolationByIds(taskIds, assetId).then((res) => res.data);
    },
    /* eslint-disable */
    GET_GROUPS({ commit, state }, options) {
      return taskService.getGroups(Object.assign({}, options || {}, state.options));

    },
    MOVE({ commit }, data) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .move(data)
          .then((res) => {
            patchSavedTask(commit, mapType(res.data));
            resolve(res);
          })
          .catch((err) => {
            // The one message for a refused move (the Kanban only reloads its board on reject).
            const message = moveRefusalMessage(err);
            if (message) Vue.prototype.$dialog.notify.error(message, MOVE_REFUSED_NOTIFY);
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },
    UPDATE({ state, commit }, data) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .update(data)
          .then((res) => {
            const updateMethod = router.currentRoute.name === 'list' ? $updateOrAdd : $updateOrAddSilent;

            var task = mapType(res.data);

            // if the color is null, set it to default for the UI to show the default color
            if (!task.color) {
              task.color = "Default";
            }

            updateMethod(state.entries?.tasks?.items ?? [], task);

            setTimeout(() => {
              updateComponents(task);
            })
            resolve(res);
          })
          .catch((err) => {
            $handleError(err);
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },
    ADD({ state, commit }, data) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .add(data)
          .then((res) => {
            const updateMethod = router.currentRoute.name === 'list' ? $updateOrAdd : $updateOrAddSilent;

            var task = mapType(res.data);
            updateMethod(state.entries?.tasks?.items ?? [], task);
            setTimeout(() => {
              addToComponents(task);
            })
            commit('SET_TOTAL', ++state.total);
            resolve(res);
          })
          .catch((err) => {
            $handleError(err);
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },
    DUPLICATE({ state, commit }, id) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .duplicate(id)
          .then((res) => {
            var task = mapType(res.data);
            $updateOrAddSilent(state.entries?.tasks?.items ?? [], task);
            setTimeout(() => {
              addToComponents(task);
            })
            commit('SET_TOTAL', ++state.total);
            resolve(task);
          })
          .catch((err) => {
            $handleError(err);
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },
    CREATE_MULTIPLE({ state, commit }, data) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .createMultiple(data)
          .then((res) => {
            var tasks = res.data.map(e => mapType(e));
            tasks.forEach(e => {
              $updateOrAddSilent(state.entries?.tasks?.items ?? [], e);
              setTimeout(() => {
                addToComponents(e);
              })
            });
            commit('SET_TOTAL', state.total + tasks.length);
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },

    DELETE({ state, commit }, ids) {
      return taskService
        .deleteCollection(ids)
        .then(() => {
          // First find all items to be deleted
          const itemsToDelete = ids.map(id => 
            (state.entries?.tasks?.items ?? []).find(e => e.id == id)
          ).filter(item => item); // Remove any undefined items
          
          // Then remove from array
          ids.forEach(id => {
            $deleteFromArray(state.entries?.tasks?.items ?? [], id);
          });
          
          // Then delete from components using the previously found items
          setTimeout(() => {
            itemsToDelete.forEach(item => deleteFromComponents(item));
          });
 
          commit('SET_TOTAL', state.total - ids.length);
        })
        .catch((err) => {
          $handleError(err);
        });
    },

    BULK({ state, commit }, data) {
      return new Promise((resolve, reject) => {
        commit('SET_UPDATE_LOADING', true);
        taskService
          .bulk(data)
          .then((res) => {
            var tasks = res.data.map(e => mapType(e));

            const updateMethod = router.currentRoute.name === 'list' ? $updateOrAdd : $updateOrAddSilent;

            tasks.forEach(task => {
              updateMethod(state.entries?.tasks?.items ?? [], task);

            })
            setTimeout(() => {
              tasks.forEach(task => {
                updateComponents(task);
              })
            })
            resolve(res);
          })
          .catch((err) => {
            $handleError(err);
            reject(err);
          })
          .finally(() => {
            commit('SET_UPDATE_LOADING', false);
          });
      });
    },

    GET_COLLECTION({ state, commit }, ids) {
      return taskService
        .getCollection(ids)
        .then((res) => {
          commit('SET_ENTRIES_COLLECTION', res);
        })
        .catch((err) => {
          $handleError(err);
        });
    },

    REORDER_GANTT_TASK({ state, commit }, data) {
      return taskService.reorderGanttTask(data);
    },
  },
};

/**
 * Reads the board's tasks into the store. `quiet` leaves the loading line off (a background refresh).
 * Shared by GET_DATA and GET_DATA_QUIETLY.
 */
function loadBoard({ commit, state }, options, { quiet = false } = {}) {
  if (!quiet) commit('SET_LOADING', true);
  const mergedOptions = Object.assign({}, options || {}, state.options);
  return taskService
    .getData(mergedOptions)
    .then((res) => {
      var data = res.data;
      data.tasks.items = data.tasks.items.map((e) => mapType(e));

      commit('SET_ENTRIES', data);
      commit('SET_TOTAL', data.tasks.total);
      // Track which taskTypeId is currently in `entries` so the route
      // watcher can skip heavy refetches when round-tripping deep-links.
      if (mergedOptions?.taskTypeId) {
        commit('SET_LAST_LOADED_TASK_TYPE_ID', mergedOptions.taskTypeId);
      }
    })
    .catch((err) => {
      $handleError(err);
    })
    .finally(() => {
      if (!quiet) commit('SET_LOADING', false);
    });
}

/**
 * A task the server saved, patched into the board in place: the store's list (a known task is replaced without
 * a re-render; a new one is added, so the Kanban parses it in; UPSERT_TASK), then the open Gantt, Scheduler and Kanban cards
 * (updateComponents). Shared by MOVE and PATCH_CHANGED.
 */
function patchSavedTask(commit, task) {
  commit('UPSERT_TASK', task);
  setTimeout(() => {
    updateComponents(task);
  });
}

function mapType(task) {
  task.start_date = new Date(task.start_date);
  task.end_date = new Date(task.end_date);

  if (task.customFields && task.customFields.length) {
    task.customFields.forEach(e => {
      task[e.fieldId] = e.value;
    });
  }
  return task;
}
function updateComponents(item) {
  if (gantt) {
    var found = gantt.isTaskExists(item.id);
    if (found) {
      var source = gantt.getTask(item.id);
      $updateManually(source, item);
      gantt.$data.tasksStore.refresh(item.id);
      gantt._quickRefresh(function () {
        gantt.refreshTask(item.id);
      });
    }
  }
  if (scheduler && scheduler.getEvent(item.id)) {
    let event = scheduler.getEvent(item.id);
    event = Object.assign(event, item);
    scheduler.updateEvent(event.id);

  }
  var board = window.board;
  if (board) {
    let found = board.getCard(item.id);
    if (found) {
      board.updateCard({ id: item.id, card: item, replace: true, skipProvider: true });
    }

  }
}

function deleteFromComponents(item) {
  item.app_handled = true;
  if (gantt) {
    var found = gantt.isTaskExists(item.id);
    if (found) gantt.deleteTask(item.id);

  }
  if (scheduler) {
    let found = scheduler.getEvent(item.id);
    if (found) scheduler.deleteEvent(item.id);

  }
  var board = window.board;
  if (board) {
    let found = board.getCard(item.id);
    if (found) board.deleteCard(item);

  }
}
function addToComponents(item) {
  item.app_handled = true;
  if (gantt) {
    var notFound = !gantt.isTaskExists(item.id);
    if (notFound) gantt.addTask(item, item.parent);

  }
  if (scheduler) {
    let notFound = !(scheduler.getEvent(item.id));
    if (notFound) scheduler.addEvent(item);

  }
  var board = window.board;
  if (board) {
    let notFound = !(board.getCard(item.id));

    if (notFound) {
      board.addCard(item);
    }
  }
}